import { useCallback } from 'react';
import { useApiCall } from '@/hooks/useApiCall';
import { useApiClient } from '@/hooks/useApiClient';
import { trListGaExperiments, trGetGaExperimentResults } from '../api/ToolRecommenderApiClient';
import type { GaExperimentSummary, GaExperimentResult } from '../types/ToolRecommenderTypes';
import type { BaseResponse } from '@/lib/http/HttpClient';

type ExperimentsResponse = BaseResponse & { _list: GaExperimentSummary[] };
type ExperimentResultResponse = BaseResponse & { _result: GaExperimentResult };

export function useGaExperiments() {
  const http = useApiClient();

  const apiFn = useCallback(
    async (signal: AbortSignal) => {
      if (!http) return { ok: false as const, code: 0, name: 'NoClient', message: '', timestamp: '' };
      const list = await trListGaExperiments(http, signal);
      return { ok: true as const, data: { _list: list, timestamp: '' } };
    },
    [http]
  );

  const { data, isLoading, error, retry } = useApiCall<ExperimentsResponse>({
    apiFn,
    deps: [http],
  });

  return {
    experiments: data?._list ?? null,
    isLoading,
    error,
    retry,
    refresh: retry,
  };
}

export function useGaExperimentDetail(experimentId: string | undefined) {
  const http = useApiClient();

  const apiFn = useCallback(
    async (signal: AbortSignal) => {
      if (!http || !experimentId)
        return { ok: false as const, code: 0, name: 'NoClient', message: '', timestamp: '' };
      const result = await trGetGaExperimentResults(http, experimentId, signal);
      return { ok: true as const, data: { _result: result, timestamp: '' } };
    },
    [http, experimentId]
  );

  const { data, isLoading, error, retry } = useApiCall<ExperimentResultResponse>({
    apiFn,
    deps: [http, experimentId],
    immediate: !!experimentId,
  });

  return {
    result: data?._result ?? null,
    isLoading,
    error,
    retry,
  };
}
